import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router';
import {
  acceptStandardTerms,
  checkStatus,
  createSigningSession,
  type SubOnboardingStatusResponse,
} from '../api';
import type {
  PaymentTermsDays,
  PublicAgreementStatus,
  SubAgreementPublicState,
  SubmissionAgreementStatus,
} from '@hts/shared';
import { isNegotiatedPaymentTermsDays } from '@hts/shared';

// Bundled with the app, not pulled from Webflow's CDN. The company is off Webflow;
// an external host we no longer pay for is one lapsed invoice away from a
// broken logo on a customer-facing page.
const LOGO_URL = '/hts-logo-white.png';

const AUTO_REFRESH_INTERVAL_MS = 30_000;

// MUL-3919 §6.2: one view per SubAgreementPublicState. Headings + body copy
// are authoritative per the contract; only the "ready_to_sign" / "signed" /
// "complete" branches keep the existing flow unchanged.
interface PublicViewModel {
  heading: string;
  body: React.ReactNode;
  badgeLabel: string;
  tone: 'review' | 'rejected' | 'confirming' | 'delayed' | 'signing' | 'complete';
}

function viewModelFor(
  agreement: PublicAgreementStatus,
  fallbackStatus: string,
): PublicViewModel {
  const state: SubAgreementPublicState = agreement.state;
  const days = agreement.requestedPaymentTermsDays;
  const requestedLabel = isNegotiatedPaymentTermsDays(days) ? `Net ${days}` : 'different terms';

  switch (state) {
    case 'pending_terms_review':
      return {
        heading: "We're reviewing your payment terms request",
        body: (
          <>
            You asked for {requestedLabel} after customer payment. HTS will review it and email
            you. You don't need to do anything right now.
          </>
        ),
        badgeLabel: 'Under review',
        tone: 'review',
      };
    case 'terms_rejected':
      return {
        heading: "We can't offer the terms you requested",
        body: (
          <>
            HTS isn't able to approve {requestedLabel} for this agreement. You can continue with
            our standard terms: paid when paid.
          </>
        ),
        badgeLabel: 'Requested terms not approved',
        tone: 'rejected',
      };
    case 'confirming_issue':
      return {
        heading: "We're preparing your agreement",
        body: (
          <>
            We're confirming your agreement has been issued. This usually takes a few minutes.
            We'll email you when it's ready to sign.
          </>
        ),
        badgeLabel: 'Preparing your agreement',
        tone: 'confirming',
      };
    case 'issue_delayed':
      return {
        heading: 'Your agreement is taking longer than usual',
        body: (
          <>
            Our team has been notified and will follow up. If you contact us, please quote
            reference <strong>{agreement?.referenceId ?? 'HTS-PENDING'}</strong>.
          </>
        ),
        badgeLabel: 'Needs attention',
        tone: 'delayed',
      };
    case 'ready_to_sign':
      // The "you can open it here even if the DocuSign email did not
      // arrive" self-serve reassurance is rendered by the action block,
      // gated on `canOpen`. It must not live in the model body, because
      // `showSignButton` is true on `agreement.state === 'ready_to_sign'`
      // regardless of whether the server has actually authorised a
      // self-serve sign-in (`canOpenAgreement` is set server-side only
      // when `status === 'docusign_sent' && !!docusignEnvelopeId`). A
      // Phase-3 `ready_to_sign` with `canOpenAgreement:false` would
      // otherwise render "you can open it here" AND "we'll email you the
      // link" with no button between them. This matches the legacy
      // `docusign_sent` body at `:263` (closed against QA FAIL #2 on #1845
      // @ c968b2f9).
      return {
        heading: 'Your agreement is ready to sign',
        body: <>Your Master Subcontractor Agreement is ready in DocuSign.</>,
        badgeLabel: 'Agreement sent for signature',
        tone: 'signing',
      };
    case 'signed_pending_hts':
      return {
        heading: 'Thank you for signing',
        body: (
          <>
            The agreement is now waiting for the HTS counter-signature. You'll be notified once
            it's fully executed.
          </>
        ),
        badgeLabel: 'You signed — awaiting counter-signature',
        tone: 'signing',
      };
    case 'complete':
      return {
        heading: 'Agreement complete',
        body: (
          <>
            Thank you for signing the agreement. Our team is reviewing your application and
            will be in touch.
          </>
        ),
        badgeLabel: 'Agreement signed',
        tone: 'complete',
      };
    case 'agreement_cancelled':
      return {
        heading: 'This agreement is no longer active',
        body: (
          <>
            The DocuSign agreement was declined or cancelled, so it can't be signed. Our team has
            been notified and will contact you about next steps. If you contact us, please quote
            reference <strong>{agreement?.referenceId ?? 'HTS-PENDING'}</strong>.
          </>
        ),
        badgeLabel: 'Agreement no longer active',
        tone: 'delayed',
      };
    default:
      // The caller routes legacy payloads (no `agreement` block) to
      // `legacyViewModel()` directly, so this branch is unreachable under
      // the current call graph. If a future state is added to
      // `SubAgreementPublicState` and forgotten here, the missing-state
      // assertion will surface it at build time rather than silently
      // dropping the sub back to a generic page.
      const exhaustive: never = state;
      void exhaustive;
      void fallbackStatus;
      return {
        heading: 'Application received',
        body: <>Your application is being processed.</>,
        badgeLabel: 'Application status update',
        tone: 'review',
      };
  }
}

// Backstop copy for the legacy `GET /status/:accessToken` payload (MUL-3919
// §3.1, pre-Phase-3 API). Today the main API returns `{ status, ...,
// canOpenAgreement }` with no `agreement` block, so until #1829 lands we
// must render sensible copy for every status the legacy server can emit.
// Keep the labels and body identical to the prior main so any subcontractor
// mid-flow sees the same wording they had yesterday (closed against QA FAILs
// @ 32112f75 — `docusign_sent` / `docusign_signed` were dropping to a raw
// enum string — and @ b1824c28 — `terms_rejected` and the seven issuing-state
// statuses were collapsing to a generic "Application status update" badge
// and stripping the Accept-standard button a sub whose terms were declined
// needs to continue the workflow).
const LEGACY_BADGE_LABELS: Record<string, string> = {
  submitted: 'Submitted — Under Review',
  under_review: 'Under Review',
  pending_terms_review: 'Agreement Terms Under Review',
  terms_rejected: 'Requested Terms Not Approved',
  approved_pending_issue: 'Agreement Being Prepared',
  msa_issuing: 'Agreement Being Prepared',
  reconciling: 'Agreement Status Being Confirmed',
  retryable_failed: 'Agreement Preparation Needs Attention',
  terminal_failed: 'Agreement Preparation Needs Attention',
  docusign_sent: 'Agreement Sent for Signature',
  docusign_sub_signed: 'You Signed — Awaiting Counter-Signature',
  docusign_signed: 'Agreement Signed — Under Review',
  docusign_declined: 'Agreement Declined',
  docusign_voided: 'Agreement Cancelled',
  approved: 'Approved',
  rejected: 'Not Approved',
};

function legacyViewModel(status: SubOnboardingStatusResponse): PublicViewModel {
  // The legacy payload carries `termsRejectionReason` / `reason` instead of
  // the Phase-3 `agreement` block; we use the same precedence main used so
  // a sub whose terms were declined sees the same wording they had yesterday.
  //
  // Closed against QA FAIL #2 on #1845 @ 62e01030: the previous default said
  // "standard Net 30 terms", but on this page the confirm dialog immediately
  // below (and the server's `effectiveTerms({})`) both name **paid when paid**
  // as the standard. Two different "standard" terms on one screen at the
  // moment the sub commits to a payment schedule is a contract defect (D1 in
  // the completion contract — paid when paid is the standard agreement).
  const rejectionCopy =
    status.termsRejectionReason ||
    status.reason ||
    "We couldn't approve the requested payment schedule. You can continue with our standard paid-when-paid terms.";

  // The legacy server emits every status string from before the Phase-3
  // schema work — some of those (e.g. `under_review`, `msa_issuing`,
  // `reconciling`, `retryable_failed`, `terminal_failed`, `approved`,
  // `rejected`) are not on the `SubmissionAgreementStatus` union. Cast to
  // string here; the switch handles each branch explicitly and the default
  // catches anything else (e.g. a future status added on main before the
  // Phase-3 API merges).
  switch (status.status as string) {
    case 'submitted':
    case 'under_review':
      return {
        heading: 'Application received',
        body: (
          <>Your application is being reviewed by our team. You'll receive an email once a decision has been made.</>
        ),
        badgeLabel: LEGACY_BADGE_LABELS[status.status],
        tone: 'review',
      };
    case 'pending_terms_review':
      return {
        heading: 'Reviewing your payment terms request',
        body: (
          <>
            Awaiting our team's review of your requested terms — we'll email you when the agreement
            is ready to sign.
          </>
        ),
        badgeLabel: LEGACY_BADGE_LABELS.pending_terms_review,
        tone: 'review',
      };
    case 'terms_rejected':
      return {
        heading: "We can't offer the terms you requested",
        body: <>{rejectionCopy}</>,
        badgeLabel: LEGACY_BADGE_LABELS.terms_rejected,
        tone: 'rejected',
      };
    case 'approved_pending_issue':
    case 'msa_issuing':
    case 'reconciling':
      return {
        heading: 'Preparing your agreement',
        body: (
          <>
            We're confirming whether your agreement was issued. You don't need to resubmit your
            application.
          </>
        ),
        badgeLabel: LEGACY_BADGE_LABELS[status.status],
        tone: 'confirming',
      };
    case 'retryable_failed':
    case 'terminal_failed':
      return {
        heading: 'Your agreement is taking longer than usual',
        body: (
          <>
            We need to finish preparing your agreement. Please contact support@hts.pro and include
            your status-link reference.
          </>
        ),
        badgeLabel: LEGACY_BADGE_LABELS[status.status],
        tone: 'delayed',
      };
    case 'docusign_sent':
      // The body must not promise a self-serve sign-in the server has not
      // authorised. The "open it here even if the DocuSign email did not
      // arrive" copy is rendered by the action block, gated on
      // `canOpenAgreement` (closed against QA FAIL #3 on #1845 @ 62e01030 —
      // the legacy body was unconditional, so a sub on the legacy server with
      // `canOpenAgreement: false` read both "you can open it here" and "we'll
      // email you the link" with no button between them).
      return {
        heading: 'Your agreement is ready to sign',
        body: <>Your Master Subcontractor Agreement is ready in DocuSign.</>,
        badgeLabel: LEGACY_BADGE_LABELS.docusign_sent,
        tone: 'signing',
      };
    case 'docusign_sub_signed':
      return {
        heading: 'Thank you for signing',
        body: (
          <>
            The agreement is now waiting for the HTS counter-signature. You'll be notified once it's
            fully executed.
          </>
        ),
        badgeLabel: LEGACY_BADGE_LABELS.docusign_sub_signed,
        tone: 'signing',
      };
    case 'docusign_signed':
      return {
        heading: 'Agreement complete',
        body: (
          <>
            Thank you for signing the agreement. Our team is reviewing your application and will be
            in touch.
          </>
        ),
        badgeLabel: LEGACY_BADGE_LABELS.docusign_signed,
        tone: 'complete',
      };
    case 'docusign_declined':
    case 'docusign_voided':
      return {
        heading: 'This agreement is no longer active',
        body: (
          <>
            The DocuSign agreement was declined or cancelled, so it can't be signed. Our team has
            been notified and will contact you about next steps.
          </>
        ),
        badgeLabel: LEGACY_BADGE_LABELS[status.status],
        tone: 'delayed',
      };
    case 'approved':
      return {
        heading: 'Approved',
        body: (
          <>Your onboarding has been approved. Welcome to the HTS subcontractor network!</>
        ),
        badgeLabel: LEGACY_BADGE_LABELS.approved,
        tone: 'complete',
      };
    case 'rejected':
      return {
        heading: 'Not approved',
        body: (
          <>We weren't able to approve your application. Please contact support@hts.pro for details.</>
        ),
        badgeLabel: LEGACY_BADGE_LABELS.rejected,
        tone: 'rejected',
      };
    default:
      // Never render a raw DB enum as the badge label — a subcontractor
      // staring at the string `docusign_sent` has no path forward even if
      // the body copy is otherwise sensible. If we see an unrecognised
      // status we fall back to a generic in-progress label and the tone
      // stays `review` so the page still reads as "we are working on it".
      return {
        heading: 'Application received',
        body: <>Your application is being processed.</>,
        badgeLabel: 'Application status update',
        tone: 'review',
      };
  }
}

function describeTerms(days: PaymentTermsDays | undefined): string {
  if (days === null || days === undefined) return 'paid when paid (HTS default)';
  return `Net ${days}`;
}

export function StatusPage() {
  const { accessToken } = useParams<{ accessToken: string }>();
  const [status, setStatus] = useState<SubOnboardingStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [openingAgreement, setOpeningAgreement] = useState(false);
  const [signingError, setSigningError] = useState<string | null>(null);
  const [acceptingStandard, setAcceptingStandard] = useState(false);
  const [standardError, setStandardError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [announcement, setAnnouncement] = useState<string>('');

  const acceptButtonRef = useRef<HTMLButtonElement | null>(null);
  const confirmHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const confirmDialogRef = useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(async (silent = false) => {
    if (!accessToken) return;
    if (!silent) setLoading(true);
    try {
      const next = await checkStatus(accessToken);
      setStatus(next);
      setError(null);
      if (silent) {
        // Only announce in the confirming_issue auto-refresh case (§6.2).
        if (next.agreement?.state === 'confirming_issue') {
          setAnnouncement(`Checked status at ${new Date().toLocaleTimeString()}. Still preparing.`);
        }
      }
    } catch {
      if (!silent) setError('Submission not found. Please check your link.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [accessToken]);

  // Initial fetch
  useEffect(() => {
    void refresh(false);
  }, [refresh]);

  // MUL-3919 §6.2: auto-refresh every 30s while we're in `confirming_issue`.
  // Stop the timer when the state changes out of that view so we don't keep
  // polling a completed / signed submission forever.
  useEffect(() => {
    if (status?.agreement?.state !== 'confirming_issue') return;
    const id = window.setInterval(() => {
      void refresh(true);
    }, AUTO_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [status?.agreement?.state, refresh]);

  const openConfirm = () => {
    setStandardError(null);
    setConfirmOpen(true);
    // Focus the dialog heading on the next paint so screen readers announce
    // it and the visual focus ring lands on the right element. The heading
    // has `tabIndex={-1}` so it is focusable without entering the tab order.
    requestAnimationFrame(() => confirmHeadingRef.current?.focus());
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    // Return focus to the Accept button so keyboard users land where they
    // started (WCAG 2.4.3 focus order).
    requestAnimationFrame(() => acceptButtonRef.current?.focus());
  };

  // MUL-3919 §6.2 S5: the confirm dialog supports Escape-to-close as well
  // as the heading-focus + click-outside behaviour. A document listener (not
  // a React keydown on the dialog div) is the standard fix because the
  // heading is `tabIndex={-1}` and would otherwise swallow keystrokes
  // before they reached a keydown handler.
  useEffect(() => {
    if (!confirmOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeConfirm();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [confirmOpen]);

  // MUL-3919 §6.2 S5: Tab containment (focus trap) inside the dialog. When
  // the user Tabs past the last focusable (the Accept button), wrap to the
  // first (Cancel); Shift+Tab past Cancel wraps to Accept. Heading is
  // tabIndex={-1} so it is excluded from the cycle. This is the same
  // pattern `packages/ui`'s `Dialog` uses; we own it inline because the
  // shared component focuses the first focusable (not the heading, which
  // §6.2 S5 requires) and importing the design-system package would pull
  // in styles that this app intentionally does not use.
  //
  // Two subtle points (closed against QA FAIL on #1845 @ e7241207):
  //   1. The selector excludes disabled controls. The Accept button is
  //      `disabled` while `acceptStandardTerms` is in flight; matching it
  //      would make it `last` even though it can never receive focus, so
  //      `active === last` is unreachable and Tab escapes the dialog.
  //   2. Focus opens on the heading (tabIndex={-1}), which is NOT in the
  //      cycle. The first Tab the user presses (including Shift+Tab) has
  //      `active` outside the focusable list, so the wrap-by-`active`
  //      condition is false, `preventDefault()` never runs, and Tab walks
  //      into the page behind the modal. Seed the cycle by focusing the
  //      appropriate endpoint before letting Tab continue.
  //
  // When #1846 lands its `useFocusTrap` hook in @hts/ui with the corrected
  // selector and seed, this inline implementation can be replaced by
  // consuming the shared hook — the JSX wiring is identical.
  const handleDialogKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const dialogEl = confirmDialogRef.current;
    if (!dialogEl) return;
    const focusable = [
      ...dialogEl.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]),' +
          ' select:not([disabled]), textarea:not([disabled]),' +
          ' [tabindex]:not([tabindex="-1"])',
      ),
    ];
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;

    // Focus opens on the heading (tabIndex={-1}) or elsewhere outside the
    // cycle; seed the focus before letting the user continue, otherwise the
    // very first Tab/Shift+Tab walks into the page behind the modal.
    if (!active || !focusable.includes(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  const acceptStandard = async () => {
    if (!accessToken || acceptingStandard) return;
    setConfirmOpen(false);
    setAcceptingStandard(true);
    setStandardError(null);
    try {
      const result = await acceptStandardTerms(accessToken);
      // Optimistically refetch; the server response already carries the new
      // `state: 'confirming_issue'` so the page flips without a round trip.
      setStatus((current) =>
        current
          ? {
              ...current,
              // `standard_accepted_pending_issue` is a Phase-3 value the
              // legacy server never emits and that `LEGACY_BADGE_LABELS`
              // doesn't carry. On the legacy payload the optimistic render
              // would otherwise drop to the generic "Application status
              // update" badge for one frame between click and refresh — a
              // generic flash immediately after a contractual action
              // (closed against QA FAIL #4 on #1845 @ 62e01030). Mirror the
              // legacy server's own accepted-state value (`msa_issuing`,
              // which is in `LEGACY_BADGE_LABELS` as "Agreement Being
              // Prepared") when the Phase-3 block is absent. The legacy
              // server's pre-Phase-3 statuses are off the
              // `SubmissionAgreementStatus` union, so the `as` cast keeps
              // the optimistic render aligned with what `legacyViewModel`
              // already accepts as a runtime value.
              status: (current.agreement
                ? 'standard_accepted_pending_issue'
                : 'msa_issuing') as SubmissionAgreementStatus,
              agreement: current.agreement
                ? {
                    ...current.agreement,
                    state: 'confirming_issue',
                    canAcceptStandard: false,
                  }
                : current.agreement,
              canOpenAgreement: false,
            }
          : current,
      );
      // Notify screen readers that we kicked off the standard-issue path.
      setAnnouncement('Standard terms accepted. We are preparing your agreement.');
      // Best-effort: ensure the fresh state is what the user sees (the server
      // is also re-evaluated on refresh — see §3.1).
      void refresh(true).catch(() => {});
      // Keep the result in scope so lint + bundler don't strip the future use.
      void result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to continue with standard terms';
      setStandardError(message);
      setAnnouncingError(message);
      setAcceptingStandard(false);
      requestAnimationFrame(() => acceptButtonRef.current?.focus());
    }
  };

  const setAnnouncingError = (msg: string) => {
    setAnnouncement(msg);
  };

  const openAgreement = async () => {
    if (!accessToken || openingAgreement) return;
    setOpeningAgreement(true);
    setSigningError(null);
    try {
      const url = await createSigningSession(accessToken);
      window.location.assign(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to open agreement';
      setSigningError(message);
      setOpeningAgreement(false);
    }
  };

  const model = status
    ? status.agreement
      ? viewModelFor(status.agreement, status.status)
      : legacyViewModel(status)
    : null;
  const showSignButton =
    status != null && (status.status === 'docusign_sent' || status.agreement?.state === 'ready_to_sign');
  const canOpen = status?.canOpenAgreement ?? false;
  // Accept-standard gate: Phase-3 server puts the boolean on `agreement`;
  // main's legacy payload only signals the same state via `status: 'terms_rejected'`.
  // Without this fallback, a sub whose terms were declined under the legacy
  // payload sees no button at all (closed against QA FAIL @ b1824c28 — the
  // button is the sub's only path to a signed agreement).
  const showAcceptStandard =
    status != null &&
    (status.agreement?.canAcceptStandard === true ||
      (status.agreement == null && status.status === 'terms_rejected'));
  const requestedDays = status?.agreement?.requestedPaymentTermsDays ?? null;
  // The dialog body has to branch on whether the requested payment-terms
  // value is a real deviation (30, 45 or 60). When `requestedDays` is anything
  // else — the legacy payload (no `agreement` block), or a Phase-3 payload
  // that carries `requestedPaymentTermsDays: null` because the sub asked
  // for paid-when-paid, or any future value outside the negotiated set —
  // naming it produces "We weren't able to approve paid when paid for this
  // agreement", a sentence that contradicts itself on the very dialog the
  // sub opens to accept paid-when-paid. Use the indefinite form whenever
  // the value is not a negotiated term (closed against QA FAIL #1 on #1845 @
  // 62e01030, refined against QA FAIL #1 on #1845 @ c968b2f9 — the previous
  // `hasAgreementBlock = status?.agreement != null` guard still rendered the
  // self-contradicting sentence for an `agreement` block whose
  // `requestedPaymentTermsDays` was null).
  const canNameRequestedTerms = isNegotiatedPaymentTermsDays(requestedDays);

  return (
    <div className="app-container">
      <header className="app-header">
        <img src={LOGO_URL} alt="Harris Technology Services" />
        <h1>Subcontractor Onboarding</h1>
      </header>

      <main className="app-main">
        <div className="card status-card" data-state={model?.tone ?? 'loading'}>
          {loading ? (
            <p>Checking status...</p>
          ) : error ? (
            <>
              <h2 style={{ color: '#0D2152', marginBottom: 8 }}>Status Not Found</h2>
              <p>{error}</p>
            </>
          ) : status && model ? (
            <>
              <h2 style={{ color: '#0D2152', marginBottom: 4 }}>{status.companyName}</h2>
              <span className={`status-badge state-${model.tone}`} data-state={model.tone}>
                <span className="status-text">{model.badgeLabel}</span>
              </span>
              <p style={{ marginTop: 16, fontSize: 14, color: '#6B7280' }}>
                Submitted:{' '}
                {new Date(status.submittedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
              {status.reviewedAt && (
                <p style={{ fontSize: 14, color: '#6B7280' }}>
                  Reviewed:{' '}
                  {new Date(status.reviewedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              )}
              {/* `aria-live="polite"` so a refresh announces without
                  interrupting the user (per §6.2 + WCAG 4.1.3). */}
              <h3 style={{ marginTop: 24, color: '#0D2152', fontSize: 20 }}>{model.heading}</h3>
              <p style={{ marginTop: 8, fontSize: 14, color: '#4A4A4A' }}>{model.body}</p>

              {showAcceptStandard && (
                <div className="status-action" style={{ marginTop: 24 }} aria-live="polite">
                  <button
                    ref={acceptButtonRef}
                    className="btn btn-primary"
                    type="button"
                    onClick={openConfirm}
                    disabled={acceptingStandard}
                    aria-haspopup="dialog"
                  >
                    {acceptingStandard ? 'Preparing agreement…' : 'Accept standard terms and continue'}
                  </button>
                  <p style={{ marginTop: 12, fontSize: 13, color: '#6B7280' }}>
                    Questions? <a href="mailto:support@hts.pro">support@hts.pro</a>
                  </p>
                  {standardError && (
                    <p role="alert" style={{ marginTop: 12, fontSize: 14, color: '#B91C1C' }}>
                      {standardError}
                    </p>
                  )}
                </div>
              )}

              {showSignButton && (
                <div className="status-action" style={{ marginTop: 24 }}>
                  {canOpen ? (
                    <>
                      <button
                        className="btn btn-primary"
                        type="button"
                        onClick={openAgreement}
                        disabled={openingAgreement}
                      >
                        {openingAgreement ? 'Opening DocuSign…' : 'Review and sign agreement'}
                      </button>
                      {/* Self-serve reassurance is shown only when the sub
                          actually has a sign-in button to use it. With the
                          legacy `docusign_sent` body, this is the only place
                          that promises the button — closed against QA FAIL #3
                          on #1845 @ 62e01030. */}
                      <p style={{ marginTop: 12, fontSize: 13, color: '#1D4ED8' }}>
                        You can open it here even if the DocuSign email did not arrive.
                      </p>
                    </>
                  ) : (
                    <p style={{ fontSize: 14, color: '#1D4ED8' }}>
                      Your Master Subcontractor Agreement is ready in DocuSign. We'll email you the link.
                    </p>
                  )}
                  {signingError && (
                    <p role="alert" style={{ marginTop: 12, fontSize: 14, color: '#B91C1C' }}>
                      {signingError}
                    </p>
                  )}
                </div>
              )}

              {/* No resubmit copy in confirming_issue (§6.2) — silence is the message. */}

              {status.agreement?.state === 'issue_delayed' && (
                <div className="status-action" style={{ marginTop: 24 }}>
                  <a className="btn btn-secondary" href="mailto:support@hts.pro">
                    Email support@hts.pro
                  </a>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Live region for auto-refresh announcements; nothing user-visible.
            Kept off-screen so it doesn't shift layout but still announces. */}
        <div
          aria-live="polite"
          aria-atomic="true"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            margin: -1,
            padding: 0,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            border: 0,
          }}
        >
          {announcement}
        </div>

        {/* Acceptance confirm dialog — MUL-3919 §6.2 S5. Opens for both
            Phase-3 (`status.agreement` present) and the legacy payload
            (`status.status === 'terms_rejected'`). Without the legacy gate
            the click on the Accept button would silently no-op — the sub
            sees the button but the dialog never appears (closed against
            QA FAIL @ b1824c28). */}
        {confirmOpen && (status?.agreement || (status != null && status.status === 'terms_rejected')) && (
          <div
            className="dialog-backdrop"
            role="presentation"
            onClick={(event) => {
              if (event.target === event.currentTarget) closeConfirm();
            }}
          >
            <div
              ref={confirmDialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="accept-standard-heading"
              aria-describedby="accept-standard-body"
              className="dialog"
              onKeyDown={handleDialogKeyDown}
            >
              <h2
                id="accept-standard-heading"
                ref={confirmHeadingRef}
                tabIndex={-1}
                style={{ color: '#0D2152', fontSize: 20, marginBottom: 8 }}
              >
                Accept standard terms and continue?
              </h2>
              <p id="accept-standard-body" style={{ fontSize: 14, color: '#4A4A4A' }}>
                {canNameRequestedTerms ? (
                  <>
                    We weren't able to approve {describeTerms(requestedDays)} for this
                    agreement. You can continue with our standard terms: paid when paid. We'll
                    prepare a new agreement and email you when it's ready to sign.
                  </>
                ) : (
                  <>
                    We weren't able to approve the payment terms you requested for this
                    agreement. You can continue with our standard terms: paid when paid. We'll
                    prepare a new agreement and email you when it's ready to sign.
                  </>
                )}
              </p>
              <div className="dialog-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeConfirm}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={acceptStandard}
                  disabled={acceptingStandard}
                >
                  {acceptingStandard ? 'Preparing…' : 'Accept standard terms'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="app-footer">
        <p>
          &copy; {new Date().getFullYear()} Harris Technology Services &bull;{' '}
          <a href="https://hts.pro">hts.pro</a>
        </p>
      </footer>
    </div>
  );
}